import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  downloadWorkflowDefinition,
  fetchWorkflowDefinitionYaml,
  workflowDefinitionErrorMessage,
  workflowDefinitionFilename,
} from "../api/workflowDefinition";
import { formatWorkflowLogicYaml } from "../utils/workflowDefinitionYaml";

export default function useWorkflowYamlSnapshot({
  projectId,
  workflow,
  components,
  production,
  saving,
  viewMode,
  persistedRefreshKey,
}) {
  const [persisted, setPersisted] = useState(() => ({
    yaml: "",
    filename: workflowDefinitionFilename(workflow?.name),
  }));
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [copyStatus, setCopyStatus] = useState("");
  const requestRevisionRef = useRef(0);
  const liveYaml = useMemo(
    () => formatWorkflowLogicYaml({ workflow, components }),
    [workflow, components]
  );

  useEffect(() => {
    requestRevisionRef.current += 1;
    setPersisted({
      yaml: "",
      filename: workflowDefinitionFilename(workflow?.name),
    });
    setCopying(false);
    setExporting(false);
    setCopyStatus("");
  }, [production, projectId, workflow?.name]);

  const fetchPersisted = useCallback(async () => {
    return fetchWorkflowDefinitionYaml(projectId, {
      published: production,
      fallbackName: workflow?.name,
    });
  }, [production, projectId, workflow?.name]);

  useEffect(() => {
    if (viewMode !== "persisted") {
      setError("");
      return undefined;
    }
    if (!projectId || saving) return undefined;
    const requestRevision = ++requestRevisionRef.current;
    setLoading(true);
    setError("");
    fetchPersisted()
      .then((definition) => {
        if (requestRevision === requestRevisionRef.current) {
          setPersisted(definition);
        }
      })
      .catch((loadError) => {
        if (requestRevision === requestRevisionRef.current) {
          setError(workflowDefinitionErrorMessage(loadError));
        }
      })
      .finally(() => {
        if (requestRevision === requestRevisionRef.current) setLoading(false);
      });
    return () => {
      requestRevisionRef.current += 1;
    };
  }, [fetchPersisted, persistedRefreshKey, projectId, saving, viewMode]);

  useEffect(
    () => () => {
      requestRevisionRef.current += 1;
    },
    []
  );

  const copyPersistedYaml = useCallback(async () => {
    const requestRevision = requestRevisionRef.current;
    setCopying(true);
    setError("");
    try {
      const definition = await fetchPersisted();
      if (requestRevision !== requestRevisionRef.current) return;
      await navigator.clipboard.writeText(definition.yaml);
      if (requestRevision !== requestRevisionRef.current) return;
      setCopyStatus("Copied");
    } catch (copyError) {
      if (requestRevision !== requestRevisionRef.current) return;
      setCopyStatus("Copy failed");
      setError(workflowDefinitionErrorMessage(copyError));
    } finally {
      if (requestRevision === requestRevisionRef.current) {
        setCopying(false);
        window.setTimeout(() => {
          if (requestRevision === requestRevisionRef.current) setCopyStatus("");
        }, 1200);
      }
    }
  }, [fetchPersisted]);

  const downloadPersistedYaml = useCallback(async () => {
    const requestRevision = requestRevisionRef.current;
    setExporting(true);
    setError("");
    try {
      const definition = await fetchPersisted();
      if (requestRevision !== requestRevisionRef.current) return;
      downloadWorkflowDefinition(definition.yaml, definition.filename);
    } catch (downloadError) {
      if (requestRevision !== requestRevisionRef.current) return;
      setError(workflowDefinitionErrorMessage(downloadError));
    } finally {
      if (requestRevision === requestRevisionRef.current) setExporting(false);
    }
  }, [fetchPersisted]);

  return {
    snapshot: {
      source: viewMode,
      yaml: viewMode === "persisted" ? persisted.yaml : liveYaml,
      filename: persisted.filename,
      loading,
      error,
    },
    actions: {
      copyPersistedYaml,
      downloadPersistedYaml,
      copying,
      exporting,
      copyStatus,
    },
  };
}
